import { useState } from 'react';
import { createPackage, updatePackage } from '../api/Packages';
import { CustomActivityInclusion, CustomFlight, InsuranceDetail, TransferPricing, VisaDetail } from '../types/PackageBuilder';
import { ItineraryItem, TripInfo } from '../types/Quotation';
import { getDatePlusDays } from '../utils/date';
import { buildHotelPointsByDay, stripHotelStayPoints } from '../utils/hotelStayPoints';
import { countWords } from '../utils/text';
import { DescriptionLimits, FaqLimits, HighlightLimits } from '../components/PackageBuilder/contracts';

type Addon = { name: string; price: number; currency: string; description: string; quoteBasePrice?: number };

type Review = { date: string; user: string; rating: number; comment: string };

export type UsePackageSaveArgs = {
  trip: {
    builderMode: 'package' | 'quotation';
    tripInfo: TripInfo;
    itinerary: ItineraryItem[];
    nightsCount: number;
  };
  pricing: {
    finalTotal: number;
    strikePrice: number;
    pricePer: 'person' | 'package' | 'night';
    currency: 'INR' | 'USD';
    hotelCategory: string;
    rating: string;
    mealPlan: string;
    transferType: string;
    transferPricing: TransferPricing;
    flightsTotal: number;
    hotelsTotal: number;
    activitiesTotal: number;
    transfersTotal: number;
    addonsTotalForPackage: number;
    visaTotal: number;
    insuranceTotal: number;
    inclusionsTotal: number;
    markupType: 'percent' | 'fixed';
    markupValue: number;
    markupAmount: number;
  };
  content: {
    highlights: string[];
    highlightLimits: HighlightLimits;
    descriptionLimits: DescriptionLimits;
    faqLimits: FaqLimits;
    idealFor: string[];
    idealVendor: string;
    searchTags: string[];
    relatedDestinations: string[];
    relatedPackages: string[];
    exclusions: string[];
    addons: Addon[];
    reviews: Review[];
    faq: Array<{ question: string; answer: string }>;
    isPublished: boolean;
    visaDetails: VisaDetail[];
    insuranceDetails: InsuranceDetail[];
    customVisaDetails: VisaDetail[];
    customInsuranceDetails: InsuranceDetail[];
    customFlights: CustomFlight[];
    customActivityInclusions: CustomActivityInclusion[];
  };
  media: {
    heroImageFile: File | null;
    galleryFiles: File[];
    hotelImageFiles: Record<string, File | null>;
  };
  meta: {
    editingId: string | null;
    slug: string;
  };
};

export const usePackageSave = ({ trip, pricing, content, media, meta }: UsePackageSaveArgs) => {
  const { builderMode, tripInfo, itinerary, nightsCount } = trip;
  const {
    finalTotal,
    strikePrice,
    pricePer,
    currency,
    hotelCategory,
    rating,
    mealPlan,
    transferType,
    transferPricing,
    flightsTotal,
    hotelsTotal,
    activitiesTotal,
    transfersTotal,
    addonsTotalForPackage,
    visaTotal,
    insuranceTotal,
    inclusionsTotal,
    markupType,
    markupValue,
    markupAmount,
  } = pricing;
  const {
    highlights,
    highlightLimits,
    descriptionLimits,
    faqLimits,
    idealFor,
    idealVendor,
    searchTags,
    relatedDestinations,
    relatedPackages,
    exclusions,
    addons,
    reviews,
    faq,
    isPublished,
    visaDetails,
    insuranceDetails,
    customVisaDetails,
    customInsuranceDetails,
    customFlights,
    customActivityInclusions,
  } = content;
  const { heroImageFile, galleryFiles, hotelImageFiles } = media;
  const { editingId, slug } = meta;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  const stripQuoteBasePrice = <T extends { quoteBasePrice?: number }>(item: T): Omit<T, 'quoteBasePrice'> => {
    const { quoteBasePrice, ...rest } = item;
    return rest;
  };

  function buildPackagePayload() {
    const durationNights = nightsCount;
    const durationDays = Math.max(1, durationNights + 1);
    const resolvePlaceIndexForDay = (day: any, dayIndex: number) => {
      const explicitPlace = day?.place;
      if (explicitPlace) {
        const matchedIndex = tripInfo.places.findIndex((place) =>
          place === explicitPlace
          || (
            String(place.value || '').trim() !== ''
            && String(place.value || '').trim() === String(explicitPlace.value || '').trim()
          )
          || (
            String(place.label || '').trim() !== ''
            && String(place.label || '').trim() === String(explicitPlace.label || '').trim()
          )
        );
        if (matchedIndex !== -1) return matchedIndex;
      }

      const placeBoundaries = tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, place, idx) => {
        const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
        const end = start + Math.max(0, Number(place.nights || 0));
        acc.push({ start, end, idx });
        return acc;
      }, []);
      const hit = placeBoundaries.find((boundary) => dayIndex >= boundary.start && dayIndex < boundary.end);
      if (hit) return hit.idx;
      return Math.max(0, tripInfo.places.length - 1);
    };

    const hotels = itinerary.flatMap((day, dayIndex) => {
      const placeIndex = resolvePlaceIndexForDay(day, dayIndex);
      const place = tripInfo.places[placeIndex];
      const fallbackCity = place?.label || place?.value || '';
      const fallbackCityCode = place?.value || '';

      return (day.hotels || []).map((h: any) => {
        const { roomVariants, ...sanitized } = (h as any) || {};
        const explicitPlaceIndex = Number(h?.placeIndex ?? h?.place_index);
        return {
          ...sanitized,
          city: h.city || h.city_name || h.cityName || fallbackCity,
          city_code: h.city_code || h.cityCode || fallbackCityCode,
          placeIndex: Number.isFinite(explicitPlaceIndex) && explicitPlaceIndex >= 0 ? explicitPlaceIndex : placeIndex,
        };
      });
    });

    const flightsData = itinerary.flatMap((d, di) =>
      (d.flights || [])
        .slice()
        .sort((a, b) => {
          const ta = new Date(a.departureTime || '').getTime();
          const tb = new Date(b.departureTime || '').getTime();
          return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
        })
        .map((f) => ({
          day: di + 1,
          date: getDatePlusDays(tripInfo.startDate, di),
          airline: f.airline,
          flightNumber: f.flightNumber,
          from: f.from,
          to: f.to,
          departureTime: f.departureTime,
          arrivalTime: f.arrivalTime,
          duration: f.duration,
          stops: f.stops,
          price: f.price,
          class: f.class,
          provider: f.provider,
          name: f.name,
          aircraft: f.aircraft,
          boardingDateTime: f.boardingDateTime,
          landingDateTime: f.landingDateTime,
          fromAirportName: f.fromAirportName,
          toAirportName: f.toAirportName,
          terminal: f.terminal,
          baggage: f.baggage,
        }))
    );

    const activitiesData = itinerary.flatMap((d, di) =>
      (d.activities || [])
        .slice()
        .sort((a, b) => {
          const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
          const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
          return ta - tb;
        })
        .map((a) => ({
          day: di + 1,
          date: getDatePlusDays(tripInfo.startDate, di),
          id: a.id,
          slug: a.slug,
          name: a.name,
          description: a.description,
          duration_minutes: a.duration_minutes,
          start_time: a.start_time,
          remarks: a.remarks,
          rules: a.rules,
          picture_url: a.picture_url,
          city: a.city,
          city_code: a.city_code,
          category: a.category,
          currency: a.currency,
          from_price_paise: a.from_price_paise,
          variant_count: a.variant_count,
          variants: a.variants,
        }))
    );

    const transfersData = itinerary.flatMap((d, di) =>
      (d.transfers || [])
        .slice()
        .sort((a, b) => {
          const ta = new Date(`${a.pickupDate || ''}T${(a.pickupTime || '00:00')}:00`).getTime();
          const tb = new Date(`${b.pickupDate || ''}T${(b.pickupTime || '00:00')}:00`).getTime();
          return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
        })
        .map((t) => ({
          day: di + 1,
          date: getDatePlusDays(tripInfo.startDate, di),
          provider: t.provider,
          name: t.name,
          vehicle: t.vehicle,
          price: t.price,
          currency: t.currency,
          pickupDate: t.pickupDate,
          pickupTime: t.pickupTime,
          pickupFromAirport: t.pickupFromAirport,
          cityId: t.cityId,
          airportCode: t.airportCode,
          hotelCode: t.hotelCode,
          meta: t.meta,
        }))
    );

    const hotelPointsByDay = buildHotelPointsByDay(itinerary, tripInfo.startDate, tripInfo.places);
    const itineraryDays = itinerary.map((d, di) => {
      const dayDate = getDatePlusDays(tripInfo.startDate, di);
      const flights = (d.flights || []).slice().sort((a, b) => {
        const ta = new Date(a.departureTime || '').getTime();
        const tb = new Date(b.departureTime || '').getTime();
        return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
      });
      const activities = (d.activities || []).slice().sort((a, b) => {
        const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
        const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
        return ta - tb;
      }).map((activity) => stripQuoteBasePrice(activity));
      const transfers = (d.transfers || []).slice().sort((a, b) => {
        const ta = new Date(`${a.pickupDate || ''}T${(a.pickupTime || '00:00')}:00`).getTime();
        const tb = new Date(`${b.pickupDate || ''}T${(b.pickupTime || '00:00')}:00`).getTime();
        return (isNaN(ta) ? 0 : ta) - (isNaN(tb) ? 0 : tb);
      }).map((transfer) => stripQuoteBasePrice(transfer));
      const points: string[] = [];
      const dateOnly = (s?: string) => (typeof s === 'string' && s.length >= 10 ? s.slice(0, 10) : undefined);

      flights.forEach((f) => {
        const depDate = dateOnly(f.departureTime);
        const arrDate = dateOnly(f.arrivalTime);
        if (depDate === dayDate && f.from) {
          points.push(`Depart from ${f.from}`);
        }
        if (arrDate === dayDate && f.to) {
          points.push(`Arrival at ${f.to}`);
        }
      });
      points.push(...(hotelPointsByDay[di] || []));

      transfers.forEach((t: any) => {
        const when = t.pickupTime ? ` at ${t.pickupTime}` : '';
        const label = t.name || t.vehicle || 'Transfer';
        points.push(`Transfer: ${label}${when}`);
      });

      activities.forEach((a: any) => {
        const title = a?.name || a?.title;
        if (title) points.push(title);
      });

      const manualPoints = stripHotelStayPoints(
        Array.isArray((d as any).points) ? (d as any).points : []
      );
      const uniquePoints = Array.from(new Set([...manualPoints, ...points].filter(Boolean)));
      return {
        day: di + 1,
        title: d.title || '',
        date: dayDate,
        flights,
        activities,
        transfers,
        points: uniquePoints,
      };
    });

    const citiesWithDays = tripInfo.places.map(place => ({
      name: place.label || place.value || '',
      days: place.nights || 0
    }));

    const basePrice = builderMode === 'package'
      ? Number(finalTotal || 0)
      : Number(tripInfo.totalPrice || 0);
    const strike = builderMode === 'package'
      ? (strikePrice > basePrice ? strikePrice : undefined)
      : (strikePrice > 0 ? strikePrice : undefined);
    const priceBreakdown = {
      flights: flightsTotal,
      hotels: hotelsTotal,
      activities: activitiesTotal,
      transfers: transfersTotal,
      addons: addonsTotalForPackage,
      visa: visaTotal,
      insurance: insuranceTotal,
      subtotal: inclusionsTotal,
      markup: {
        type: markupType,
        value: Number(markupValue || 0),
        amount: markupAmount,
      },
      total: basePrice,
    };

    const addonsPayload = builderMode === 'package'
      ? addons.filter((addon) => (addon?.name || '').toLowerCase() !== 'insurance')
      : addons;
    const customisableInclusions = {
      visaDetails: customVisaDetails,
      insuranceDetails: customInsuranceDetails,
      flights: customFlights.map((flight) => {
        const { airportCode, airportName, ...rest } = flight as CustomFlight;
        return rest;
      }),
      activities: customActivityInclusions,
      transferPricing,
    };

    return {
      title: tripInfo.title,
      subtitle: tripInfo.subtitle || undefined,
      description: tripInfo.tripOverview || undefined,
      rating: rating || undefined,
      mealPlan: mealPlan || undefined,
      transferType: transferType || undefined,
      idealFor: idealFor || [],
      hotelCategory: hotelCategory || rating || undefined,
      idealVendor: idealVendor || undefined,
      searchTags: searchTags || [],
      relatedDestinations: relatedDestinations || [],
      relatedPackages: relatedPackages || [],
      cities: citiesWithDays,
      durationDays: durationDays,
      durationNights: durationNights,
      fixedDepartureDate: tripInfo.startDate,
      price: {
        base: basePrice,
        strike,
        per: builderMode === 'package' ? 'couple' : pricePer,
        currency,
        breakdown: priceBreakdown,
      },
      highlights: [...highlights],
      itinerary: itineraryDays,
      flights: flightsData,
      activities: activitiesData,
      transfers: transfersData,
      hotels,
      visaDetails: visaDetails.map((detail) => stripQuoteBasePrice(detail)),
      insuranceDetails: insuranceDetails.map((detail) => stripQuoteBasePrice(detail)),
      customisableInclusions,
      inclusions: [...tripInfo.inclusions],
      exclusions,
      addons: addonsPayload.map((addon) => stripQuoteBasePrice(addon)),
      reviews,
      faq,
      seo: {},
      isPublished: isPublished,
    };
  }

  async function savePackage() {
    setSaving(true);
    setError('');
    try {
      if (!tripInfo.title) throw new Error('Trip title is required');
      if (builderMode === 'package') {
        const wordCount = countWords(tripInfo.tripOverview || '');
        if (wordCount < descriptionLimits.min || wordCount > descriptionLimits.max) {
          throw new Error(`Package description should be between ${descriptionLimits.min} and ${descriptionLimits.max} words.`);
        }
        if (highlights.length < highlightLimits.min || highlights.length > highlightLimits.max) {
          throw new Error(`Please add ${highlightLimits.min}-${highlightLimits.max} highlights.`);
        }
        if (highlights.some((h) => h.length > highlightLimits.maxChars)) {
          throw new Error(`Each highlight must be ${highlightLimits.maxChars} characters or less.`);
        }
        if (faq.some((info) => !info.question.trim() || info.question.trim().length > faqLimits.questionMax)) {
          throw new Error(`FAQ questions must be ${faqLimits.questionMax} characters or less.`);
        }
        if (faq.some((info) => {
          const count = countWords(info.answer || '');
          return count === 0 || count > faqLimits.answerMaxWords;
        })) {
          throw new Error(`FAQ answers must be 1-${faqLimits.answerMaxWords} words.`);
        }
        const invalidDay = itinerary.findIndex((d) => {
          const points = Array.isArray((d as any).points) ? (d as any).points : [];
          return points.length < 3;
        });
        if (invalidDay !== -1) {
          throw new Error(`Day ${invalidDay + 1} must have at least 3 itinerary items.`);
        }
        if (!searchTags || searchTags.length === 0) {
          throw new Error('At least one search tag is required.');
        }
        const hasDomestic = searchTags.some((t: string) => t.startsWith('Domestic: '));
        const hasInternational = searchTags.some((t: string) => t.startsWith('International: '));
        if (hasDomestic && hasInternational) {
          throw new Error('A package cannot have both Domestic and International tags. Please choose one.');
        }
      }
      if (!slug) {
        // Slug is auto-generated on backend; keep local slug for preview only
      }
      const payload = buildPackagePayload();
      const formData = new FormData();

      formData.append('title', payload.title);
      if (payload.subtitle) formData.append('subtitle', payload.subtitle);
      if (payload.description) formData.append('description', payload.description);
      if (payload.rating) formData.append('rating', String(payload.rating));
      if (payload.mealPlan) formData.append('mealPlan', payload.mealPlan);
      if (payload.transferType) formData.append('transferType', payload.transferType);
      if (payload.hotelCategory) formData.append('hotelCategory', payload.hotelCategory);
      if (payload.idealVendor) formData.append('idealVendor', payload.idealVendor);
      formData.append('durationDays', String(payload.durationDays));
      formData.append('durationNights', String(payload.durationNights));
      formData.append('isPublished', String(payload.isPublished));
      if (payload.fixedDepartureDate) formData.append('fixedDepartureDate', payload.fixedDepartureDate);

      formData.append('cities', JSON.stringify(payload.cities || []));
      formData.append('price', JSON.stringify(payload.price));
      formData.append('price_breakdown', JSON.stringify((payload.price as any)?.breakdown || {}));
      formData.append('highlights', JSON.stringify(payload.highlights || []));
      formData.append('idealFor', JSON.stringify(payload.idealFor || []));
      formData.append('searchTags', JSON.stringify(payload.searchTags || []));
      formData.append('relatedDestinations', JSON.stringify(payload.relatedDestinations || []));
      formData.append('relatedPackages', JSON.stringify(payload.relatedPackages || []));
      formData.append('itinerary', JSON.stringify(payload.itinerary || []));
      formData.append('flights', JSON.stringify(payload.flights || []));
      formData.append('activities', JSON.stringify(payload.activities || []));
      formData.append('transfers', JSON.stringify(payload.transfers || []));
      formData.append('hotels', JSON.stringify(payload.hotels || []));
      formData.append('visaDetails', JSON.stringify((payload as any).visaDetails || []));
      formData.append('insuranceDetails', JSON.stringify((payload as any).insuranceDetails || []));
      formData.append('customisableInclusions', JSON.stringify((payload as any).customisableInclusions || {}));
      formData.append('inclusions', JSON.stringify(payload.inclusions || []));
      formData.append('exclusions', JSON.stringify(payload.exclusions || []));
      formData.append('addons', JSON.stringify(payload.addons || []));
      formData.append('reviews', JSON.stringify(payload.reviews || []));
      formData.append('faq', JSON.stringify(payload.faq || []));
      formData.append('seo', JSON.stringify(payload.seo || {}));

      if (heroImageFile) formData.append('hero_image', heroImageFile);
      if (galleryFiles && galleryFiles.length > 0) galleryFiles.forEach((f) => formData.append('gallery', f));

      const flatHotels = itinerary.flatMap((d, di) => (d.hotels || []).map((_, hi) => ({ di, hi })));
      flatHotels.forEach(({ di, hi }, idx) => {
        const key = `${di}-${hi}`;
        const file = hotelImageFiles[key];
        if (file) formData.append(`hotels[${idx}][image]`, file);
      });

      if (editingId) {
        await updatePackage(editingId, formData);
      } else {
        await createPackage(formData);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return {
    saving,
    error,
    savePackage,
    buildPackagePayload,
  };
};

export type UsePackageSaveResult = ReturnType<typeof usePackageSave>;
